import Link from "next/link";
import dayjs from "dayjs";
import React, { useMemo, useState } from "react";

type ProductUpdateNode = {
  title?: string;
  slug?: string;
  date?: string;
  featuredImage?: { node?: { sourceUrl?: string; altText?: string } };
  acf_productUpdate?: { publishDate?: string };
};

type Props = {
  selectedSlug?: string;
  data?: { edges?: Array<{ node: ProductUpdateNode }> } | null;
};

/**
 * Related section for product-update singles — same visual layout as RelatedPosts (related-blog-v3).
 */
export default function RelatedProductUpdates({ selectedSlug, data }: Props) {
  const [email, setEmail] = useState("");
  const [emailErr, setEmailErr] = useState(false);
  const [subscribeSuccess, setSubscribeSuccess] = useState(false);

  const relatedEntries = useMemo(() => {
    const edges = data?.edges ?? [];
    return edges
      .map((e) => e.node)
      .filter((node) => node?.slug && node.slug !== selectedSlug)
      .slice(0, 4);
  }, [data?.edges, selectedSlug]);

  const handleSubscribe = (e: React.FormEvent) => {
    e.preventDefault();
    const emailRegEx =
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    const isInvalid =
      email.length < 2 || !emailRegEx.test(String(email).toLowerCase());
    if (isInvalid) {
      setEmailErr(true);
      return;
    }
    setEmailErr(false);
    setSubscribeSuccess(true);
    setEmail("");
    setTimeout(() => setSubscribeSuccess(false), 3000);

    const xhr = new XMLHttpRequest();
    const url =
      "https://api.hsforms.com/submissions/v3/integration/submit/25310090/1d995a7c-cbe1-49aa-ae7c-0a94659c24ab";
    const payload = {
      submittedAt: Date.now(),
      fields: [{ name: "email", value: email }],
      context: {
        pageUri: typeof window !== "undefined" ? window.location.href : "",
        pageName: "Product Updates",
      },
    };
    xhr.open("POST", url);
    xhr.setRequestHeader("Content-Type", "application/json");
    xhr.send(JSON.stringify(payload));
  };

  if (!relatedEntries.length) return null;

  return (
    <div className="related-blog-v3">
      <div className="container">
        <h2>More product updates</h2>
        <div className="related-blog-v3-grid">
          <div className="related-articles">
            {relatedEntries.map((item) => {
              const dateLabel =
                item?.acf_productUpdate?.publishDate ||
                (item?.date
                  ? dayjs(item.date).format("D MMM YYYY")
                  : null);
              return (
                <article
                  className="related-article-card"
                  key={item.slug}
                >
                  <Link href="/product-updates/" prefetch={false}>
                    <div className="category product-updates">
                      Product Updates
                    </div>
                  </Link>
                  <Link
                    href={`/product-updates/${item.slug}/`}
                    itemProp="url"
                    prefetch={false}
                  >
                    <h4>{item.title}</h4>
                  </Link>
                  {dateLabel ? (
                    <p className="related-article-date">{dateLabel}</p>
                  ) : null}
                </article>
              );
            })}
          </div>

          <div className="related-subscribe-card">
            <img
              src="/images/blog/v2/subscribe-deco-1.svg"
              alt="Subscribe decoration"
              className="related-subscribe-deco deco-1"
              aria-hidden="true"
            />
            <img
              src="/images/blog/v2/subscribe-deco-2.svg"
              alt="Subscribe decoration"
              className="related-subscribe-deco deco-2"
              aria-hidden="true"
            />
            <h3>
              Subscribe to
              <br />
              get our latest
              <br />
              updates
            </h3>
            <form onSubmit={handleSubscribe}>
              <input
                type="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (emailErr) setEmailErr(false);
                }}
                placeholder="Enter your email address"
                className={emailErr ? "is-error" : ""}
              />
              <button type="submit">Subscribe</button>
            </form>
            {(emailErr || subscribeSuccess) && (
              <p className={subscribeSuccess ? "ok" : "err"}>
                {subscribeSuccess
                  ? "Thank you for subscribing!"
                  : "Please enter a valid email."}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
